import { Box } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

import { MentionPosition } from 'shared/features/mentions/types';
import { CommentForm, CommentImage } from 'shared/features/posts';
import { ViewMoreButton } from 'shared/features/posts/components/ViewMoreButton';
import {
  OnCommentAdd,
  OnCommentRemove,
  OnLikeAdd,
  OnReactionRemove,
} from 'shared/hooks/useStreamChatActivity';
import { CommentEnrichedReaction, StreamChatEnrichedActivity } from 'shared/types';

import { EventComment } from './EventComment';

interface EventCommentProps {
  onCommentAdd: OnCommentAdd;
  onLikeAdd: OnLikeAdd;
  onReactionRemove: OnReactionRemove;
  onCommentRemove: OnCommentRemove;
  activity: StreamChatEnrichedActivity | undefined;
}

export function EventComments({
  onCommentAdd,
  onLikeAdd,
  onCommentRemove,
  onReactionRemove,
  activity,
}: EventCommentProps) {
  const [viewAllComments, setViewAllComments] = useState(false);
  const [comments, setComments] = useState<CommentEnrichedReaction[]>([]);

  const textFieldRef = useRef(null);

  const topicAuthorId = useMemo(() => activity?.actor.toString().split(':')[1], [activity?.actor]);

  const onCommentPost = async (
    text: string,
    image: CommentImage[],
    mentions: MentionPosition[] = [],
  ) => {
    if (!activity?.id) {
      return;
    }

    const comment = await onCommentAdd({
      activityId: activity?.id,
      text,
      media: image,
      parentEntityAuthorId: topicAuthorId,
      parentEntityType: 'topic',
      mentions,
    });

    if (!comment) {
      return;
    }

    setComments([...comments, comment]);
  };

  useEffect(() => {
    const newComments = activity?.latest_reactions?.comment || [];

    setComments(newComments);
  }, [activity?.latest_reactions?.comment]);

  if (!activity?.id) {
    return null;
  }

  const commentsAmount = comments.length - 1;

  return (
    <Box display="flex" flexDirection="column" gap="1rem">
      {comments[0] && (
        <Box key={comments[0].id}>
          <EventComment
            comment={comments[0]}
            onCommentRemove={onCommentRemove}
            onCommentAdd={onCommentAdd}
            onLikeAdd={onLikeAdd}
            onReactionRemove={onReactionRemove}
          />
        </Box>
      )}

      {viewAllComments &&
        comments.map(comment => (
          <Box key={comment.id}>
            <EventComment
              comment={comment}
              onCommentAdd={onCommentAdd}
              onCommentRemove={onCommentRemove}
              onLikeAdd={onLikeAdd}
              onReactionRemove={onReactionRemove}
            />
          </Box>
        ))}

      {!viewAllComments && commentsAmount > 1 && (
        <ViewMoreButton
          onViewMore={() => setViewAllComments(true)}
          type="comment"
          amount={commentsAmount}
        />
      )}

      <CommentForm type="comment" fieldRef={textFieldRef} onCommentPost={onCommentPost} />
    </Box>
  );
}
